import React, { memo, MouseEvent, useCallback, useMemo, useState } from 'react'
import tw from 'twin.macro'
import { ActionButton } from 'src/components/shared/ActionButton'
import { ActionMenu } from 'src/components/shared/ActionMenu'
import { MenuActionItem } from 'src/interfaces/ui'
import { useDispatch, useSelector } from 'react-redux'
import { FetchCustomerProfile, Logout } from 'src/constants/actionTypes'
import { LogOutMinor, ProfileMinor, RiskMinor, SettingsMajor } from '@shopify/polaris-icons'
import { getIsCustomerOnboarded, getIsMasterContractor } from 'src/selectors/user'
import { useLocation, useNavigate } from 'react-router-dom'
import { ROUTES } from 'src/constants/routes'
import { GSIcon } from '../shared/GSIcon'
import { createAsyncAction } from 'src/utils/reduxUtils'
import {
  getIsCustomerProfileHasExpiredSteps,
  getIsCustomerProfileHasExpiringSoonSteps,
} from 'src/selectors/customer'
import { colors } from 'src/theme'
import { CustomerCollectedDataStatus } from 'src/constants/customer/enums'
import { CustomerStepStatusChip } from '../customer/shared/CustomerStepStatusChip'

interface Props {
  logoutOnly?: boolean
}

const PartnersProfileButtonComponent = ({ logoutOnly }: Props) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const isMasterContractor = useSelector(getIsMasterContractor)
  const isCustomerOnboarded = useSelector(getIsCustomerOnboarded)
  const customerProfileHasExpiringSoonSteps = useSelector(getIsCustomerProfileHasExpiringSoonSteps)
  const customerProfileHasExpiredSteps = useSelector(getIsCustomerProfileHasExpiredSteps)

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const isMenuOpen = useMemo(() => Boolean(anchorEl), [anchorEl])

  const handleClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const actionItems: Array<MenuActionItem> = useMemo(() => {
    const res = []
    const logoutItem = {
      label: 'Log out',
      icon: <LogOutMinor width={20} height={20} css={tw`rotate-180`} />,
      onClick: () => {
        dispatch(Logout.request(null))
        setAnchorEl(null)
      },
    }

    if (logoutOnly) {
      return [logoutItem]
    }

    if (isCustomerOnboarded) {
      res.push({
        label: 'Settings',
        icon: <SettingsMajor width={16} height={16} />,
        onClick: () => {
          navigate(ROUTES.SETTINGS_URL, { state: { background: location } })

          setAnchorEl(null)
        },
      })
    }

    if (isCustomerOnboarded && isMasterContractor) {
      res.push({
        label: 'Compliance',
        icon: <GSIcon iconName="gs_notepad" />,
        rightIcon:
          customerProfileHasExpiringSoonSteps || customerProfileHasExpiredSteps ? (
            <div css={tw`ml-6`}>
              <CustomerStepStatusChip
                status={CustomerCollectedDataStatus.Expired}
                expiresSoon={customerProfileHasExpiringSoonSteps || !customerProfileHasExpiredSteps}
              />
            </div>
          ) : undefined,
        onClick: async () => {
          setAnchorEl(null)

          await createAsyncAction(dispatch, FetchCustomerProfile.request())

          navigate(ROUTES.CUSTOMER_COMPLIANCE_URL, { state: { background: location } })
        },
      })
    }

    res.push(logoutItem)

    return res
  }, [
    logoutOnly,
    location,
    isCustomerOnboarded,
    isMasterContractor,
    customerProfileHasExpiringSoonSteps,
    customerProfileHasExpiredSteps,
    navigate,
  ])

  return (
    <>
      <ActionButton
        selected={!!anchorEl}
        className={tw`px-4`}
        color="dark"
        text="Customer's Account"
        icon={
          customerProfileHasExpiringSoonSteps || customerProfileHasExpiredSteps ? (
            <RiskMinor
              width={20}
              fill={
                customerProfileHasExpiredSteps ? colors.COLORS_RED_500 : colors.COLORS_ORANGE_500
              }
            />
          ) : (
            <ProfileMinor fill="white" width={20} />
          )
        }
        onClick={handleClick}
      />
      <ActionMenu
        anchorEl={anchorEl}
        items={actionItems}
        isOpen={isMenuOpen}
        itemClassName={tw`min-w-[242px]`}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onClose={handleCloseMenu}
      />
    </>
  )
}

export const PartnersProfileButton = memo(PartnersProfileButtonComponent)
